import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import ProductGallery from '../../components/ProductGallery/ProductGallery';
import '../../components/ProductGallery/ProductGallery.css';
import './product.css';
import { useLanguage } from '../../context/LanguageContext';

const Product = ({ item }) => {
  const { lang, t } = useLanguage();
  const [productData, setProductData] = useState(null);
  const [shortDescription, setShortDescription] = useState('');
  const [sections, setSections] = useState({});
  const [activeTab, setActiveTab] = useState('features');

  useEffect(() => {
    if (!item || !item.slug) return;

    // Dile göre md dosyası yolu
    const mdPath = lang === 'en'
      ? `/products/en/${item.slug}.md`
      : `/products/${item.slug}.md`;

    fetch(mdPath)
      .then(res => {
        if (res.ok) return res.text();
        // EN dosyası yoksa TR'ye fallback
        if (lang === 'en') return fetch(`/products/${item.slug}.md`).then(r => r.ok ? r.text() : Promise.reject());
        throw new Error('MD not found');
      })
      .then(text => {
        const frontmatterRegex = /^---\s*\n([\s\S]*?)\n---\s*\n([\s\S]*)$/;
        const match = text.match(frontmatterRegex);
        if (match) {
          const frontmatter = match[1];
          const content = match[2];
          const data = {};
          let currentArrayKey = null;

          frontmatter.split('\n').forEach(line => {
            const trimmed = line.trim();
            if (trimmed.startsWith('-')) {
              if (currentArrayKey && data[currentArrayKey]) {
                const value = trimmed.substring(1).trim().replace(/^["']|["']$/g, '');
                data[currentArrayKey].push(value);
              }
            } else if (trimmed.includes(':')) {
              const colonIndex = trimmed.indexOf(':');
              const key = trimmed.substring(0, colonIndex).trim();
              const value = trimmed.substring(colonIndex + 1).trim();
              if (value === '' || value === '[]') {
                data[key] = [];
                currentArrayKey = key;
              } else {
                data[key] = value.replace(/^["']|["']$/g, '');
                currentArrayKey = null;
              }
            }
          });

          // PDF linkini her zaman Türkçe MD'den al (İngilizce versiyon yokken)
          data.pdf_form = data.pdf_form || item.pdf_form;

          setProductData(data);
          parseMarkdownSections(content);
        }
      })
      .catch(() => {
        setProductData(item);
      });
  }, [item, lang]);

  const parseMarkdownSections = (content) => {
    const lines = content.split('\n');
    let currentSection = 'short';
    let currentContent = [];
    const parsedSections = {};

    lines.forEach((line, index) => {
      if (line.startsWith('# ') && index < 5) return;

      // TR + EN section başlıkları her ikisi de match ediliyor
      if (line.startsWith('## ') && currentSection === 'short') {
        if (currentContent.length > 0) { setShortDescription(currentContent.join('\n')); currentContent = []; }
        currentSection = 'features'; return;
      }
      if (line.match(/^## (Ürün Özellikleri|Product Specifications|Product Features)/)) {
        if (currentContent.length > 0 && currentSection === 'short') setShortDescription(currentContent.join('\n'));
        currentContent = []; currentSection = 'features'; return;
      }
      if (line.match(/^## (Kullanım Alanı|Indications|Usage Area)/)) {
        if (currentContent.length > 0) parsedSections[currentSection] = currentContent.join('\n');
        currentContent = []; currentSection = 'usage'; return;
      }
      if (line.match(/^## (İçerik|Bileşimi|İçerik \(1 Tablet\)|Bileşimi \(1 Tablet\)|Composition|Bileşimi \(per 1g\)|İçerik \(per 1g\))/)) {
        if (currentContent.length > 0) parsedSections[currentSection] = currentContent.join('\n');
        currentContent = []; currentSection = 'composition'; return;
      }
      if (line.match(/^## (Dozaj Tablosu|Dozaj ve Uygulama Tablosu|Dozaj|Dosage & Administration|Dozaj & Kullanım)/)) {
        if (currentContent.length > 0) parsedSections[currentSection] = currentContent.join('\n');
        currentContent = []; currentSection = 'dosage_table'; return;
      }
      if (line.match(/^## (Kullanım Talimatları|Kullanım|Directions for Use|Dozaj ve Kullanım)/)) {
        if (currentContent.length > 0) parsedSections[currentSection] = currentContent.join('\n');
        currentContent = []; currentSection = 'dosage'; return;
      }
      if (line.match(/^## (Etken Maddeler|Active Ingredients|Etken Maddeler ve Faydaları)/)) {
        if (currentContent.length > 0) parsedSections[currentSection] = currentContent.join('\n');
        currentContent = []; currentSection = 'ingredients'; return;
      }
      if (line.match(/^## (Saklama|Storage)/)) {
        if (currentContent.length > 0) parsedSections[currentSection] = currentContent.join('\n');
        currentContent = []; currentSection = 'storage'; return;
      }

      currentContent.push(line);
    });

    if (currentContent.length > 0) {
      if (currentSection === 'short') setShortDescription(currentContent.join('\n'));
      else parsedSections[currentSection] = currentContent.join('\n');
    }
    setSections(parsedSections);
  };

  const displayData = productData || item;
  const images = displayData?.images || [displayData?.proImg || item?.proImg];
  // PDF linki her zaman orijinal (Türkçe) kaynaktan gelir
  const pdfLink = item?.pdf_form || displayData?.pdf_form;

  return (
    <>
      <div className="back-to-products">
        <Link to="/shop" className="back-btn">
          <i className="ti-arrow-left"></i> {t('product.backBtn')}
        </Link>
      </div>
      <div className="row">
        <div className="col col-lg-5 col-12">
          <ProductGallery images={images} />
        </div>
        <div className="col col-lg-7 col-12">
          <div className="product-details">
            <h2>{displayData?.title || item?.title}</h2>
            <div className="product-rt">
              <a
                href={`https://wa.me/905317755754?text=${encodeURIComponent(
                  t('product.whatsappMsg').replace('{title}', displayData?.title || item?.title || '')
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                className="whatsapp-product-btn"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="20" height="20" fill="white" style={{ marginRight: '8px', flexShrink: 0 }}>
                  <path d="M16 0C7.163 0 0 7.163 0 16c0 2.822.736 5.478 2.027 7.784L0 32l8.418-2.007A15.93 15.93 0 0 0 16 32c8.837 0 16-7.163 16-16S24.837 0 16 0zm0 29.333a13.28 13.28 0 0 1-6.77-1.853l-.485-.29-5.002 1.193 1.27-4.868-.317-.5A13.244 13.244 0 0 1 2.667 16C2.667 8.636 8.636 2.667 16 2.667S29.333 8.636 29.333 16 23.364 29.333 16 29.333zm7.274-9.93c-.398-.199-2.356-1.163-2.72-1.296-.365-.133-.63-.199-.895.199-.265.398-1.029 1.296-1.261 1.562-.232.265-.465.298-.863.1-.398-.2-1.681-.62-3.2-1.977-1.183-1.056-1.982-2.36-2.213-2.758-.232-.398-.025-.613.174-.811.179-.178.398-.465.597-.697.199-.232.265-.398.398-.663.133-.265.066-.497-.033-.697-.1-.199-.895-2.16-1.227-2.957-.323-.776-.651-.671-.895-.683l-.762-.013c-.265 0-.697.1-1.062.497-.365.398-1.394 1.362-1.394 3.322s1.427 3.852 1.626 4.117c.199.265 2.808 4.288 6.804 6.013.951.411 1.694.656 2.273.84.955.303 1.824.26 2.512.158.767-.114 2.356-.963 2.688-1.894.332-.93.332-1.728.232-1.894-.099-.166-.365-.265-.763-.464z" />
                </svg>
                {t('product.whatsapp')}
              </a>
            </div>

            {shortDescription && (
              <div className="short-description">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{shortDescription}</ReactMarkdown>
              </div>
            )}

            <div className="tg-btm">
              {displayData?.brand && <p><span>{t('product.brand')}</span>{displayData.brand}</p>}
              {displayData?.size && <p><span>{t('product.size')}</span>{displayData.size}</p>}
            </div>

            {(displayData?.marketing_title || displayData?.marketing_desc) && (
              <div className="marketing-section" style={{ marginTop: '20px', borderTop: '1px solid #eee', paddingTop: '20px' }}>
                {displayData.marketing_title && <h3 style={{ fontSize: '20px', marginBottom: '10px' }}>{displayData.marketing_title}</h3>}
                {displayData.marketing_desc && <p>{displayData.marketing_desc}</p>}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <div className="product-tabs-section">
            <ul className="nav nav-tabs">
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === 'features' ? 'active' : ''}`}
                  onClick={() => setActiveTab('features')}
                >
                  {t('product.tabFeatures')}
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === 'instructions' ? 'active' : ''}`}
                  onClick={() => setActiveTab('instructions')}
                >
                  {t('product.tabUsage')}
                </button>
              </li>
              {pdfLink && (
                <li className="nav-item">
                  <a
                    className="nav-link"
                    href={pdfLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ backgroundColor: '#ff5b2e', color: '#fff', marginLeft: '10px', borderRadius: '5px' }}
                  >
                    <i className="fi flaticon-pdf" style={{ marginRight: '5px' }}></i> {t('product.tabPdf')}
                  </a>
                </li>
              )}
            </ul>

            <div className="tab-content">
              {activeTab === 'features' && (
                <div className="tab-pane active">
                  {sections.features && (
                    <div className="product-section">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{sections.features}</ReactMarkdown>
                    </div>
                  )}
                  {sections.composition && (
                    <div className="product-section">
                      <h3>{t('product.composition')}</h3>
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{sections.composition}</ReactMarkdown>
                    </div>
                  )}
                  {sections.ingredients && (
                    <div className="product-section">
                      <h3>{t('product.ingredients')}</h3>
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{sections.ingredients}</ReactMarkdown>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'instructions' && (
                <div className="tab-pane active">
                  {sections.usage && (
                    <div className="product-section">
                      <h3>{t('product.usageArea')}</h3>
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{sections.usage}</ReactMarkdown>
                    </div>
                  )}
                  {sections.dosage && (
                    <div className="product-section">
                      <h3>{t('product.usageInstructions')}</h3>
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{sections.dosage}</ReactMarkdown>
                    </div>
                  )}
                  {sections.dosage_table && (
                    <div className="product-section">
                      <h3>{t('product.dosage')}</h3>
                      <div className="dosage-table-wrapper">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{sections.dosage_table}</ReactMarkdown>
                      </div>
                    </div>
                  )}
                  {sections.storage && (
                    <div className="product-section">
                      <h3>{t('product.storage')}</h3>
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{sections.storage}</ReactMarkdown>
                    </div>
                  )}
                  {!sections.usage && !sections.dosage && !sections.dosage_table && !sections.storage && (
                    <div className="product-section">
                      <p>{t('product.noUsage')}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Product;
